import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import {
    type AccountInfo,
    type AuthenticationResult,
    type EndSessionRequest,
    type SilentRequest
} from '@azure/msal-browser';
import { map, Observable, tap, throwError } from 'rxjs';
import { apiTokenRequest, loginRequest } from '../auth/auth.config';

@Injectable({ providedIn: 'root' })
export class SessionService {
    constructor(private readonly msalService: MsalService) {}

    initialize(): Observable<void> {
        return this.msalService.handleRedirectObservable().pipe(
            tap((result) => {
                if (result?.account) {
                    this.msalService.instance.setActiveAccount(result.account);
                }

                if (!this.msalService.instance.getActiveAccount()) {
                    const [firstAccount] = this.msalService.instance.getAllAccounts();
                    if (firstAccount) {
                        this.msalService.instance.setActiveAccount(firstAccount);
                    }
                }
            }),
            map(() => void 0)
        );
    }

    isAuthenticated(): boolean {
        return this.getActiveAccount() !== null;
    }

    getActiveAccount(): AccountInfo | null {
        return this.msalService.instance.getActiveAccount() ?? null;
    }

    getDisplayName(): string | null {
        const account = this.getActiveAccount();
        return account?.name ?? account?.username ?? null;
    }

    getAccessToken(): Observable<string> {
        const account = this.getActiveAccount();
        if (!account) {
            return throwError(() => new Error('Cannot acquire an access token without an active account.'));
        }

        const tokenRequest: SilentRequest = {
            account,
            scopes: apiTokenRequest.scopes
        };

        return this.msalService.acquireTokenSilent(tokenRequest).pipe(
            map((result) => {
                if (!result.accessToken) {
                    throw new Error('MSAL did not return an access token for the active account.');
                }

                return result.accessToken;
            })
        );
    }

    signIn(): Observable<AuthenticationResult | void> {
        return this.msalService.loginRedirect(loginRequest);
    }

    signOut(): Observable<void> {
        const account = this.getActiveAccount() ?? undefined;
        const logoutRequest: EndSessionRequest = {
            account
        };

        return this.msalService.logoutRedirect(logoutRequest);
    }
}
